import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { Copy, Check, Send } from "lucide-react";

export default function App() {
    return <WhoDidItApp />;
}

const WS_URL = getEnvWSUrl();
const APP_NAME = "Who did it (LoL Edition)";

function getEnvWSUrl() {
    // Use the backend server URL
    if (process.env.NODE_ENV === 'production') {
        return window.location.origin;
    }
    return "http://localhost:3001"; // Changed from 3000 to 3001 for Docker API
}

function useQuery() {
    return useMemo(() => new URLSearchParams(window.location.search), []);
}

function ensureRoomInUrl(room, player) {
    const url = new URL(window.location.href);
    url.searchParams.set("room", room);
    if (player) url.searchParams.set("player", String(player));
    const as = url.toString();
    if (as !== window.location.href) {
        window.history.replaceState({}, "", as);
    }
}

function makeRoomId() {
    return Math.random().toString(36).slice(2, 10);
}

function useRoomTransport(room, onMessage) {
    const socketRef = useRef(null);
    const onMessageRef = useRef(onMessage);

    // Keep the callback ref updated without triggering reconnections
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        const socket = io(WS_URL, {
            transports: ['websocket', 'polling']
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('Connected to Socket.IO server');
            socket.emit('whodiddit:join', room);
        });

        socket.on('whodiddit:message', (msg) => {
            console.log('Received message:', msg);
            onMessageRef.current(msg);
        });

        socket.on('disconnect', () => {
            console.log('Disconnected from Socket.IO server');
        });

        return () => {
            socket.disconnect();
        };
    }, [room]);

    return {
        send(obj) {
            if (socketRef.current && socketRef.current.connected) {
                socketRef.current.emit('whodiddit:message', { room, message: obj });
            }
        }
    };
}

function WhoDidItApp() {
    const q = useQuery();
    const [room] = useState(() => q.get("room") || makeRoomId());
    const [shared, setShared] = useState(null);
    const [lastSeenSeq, setLastSeenSeq] = useState(0);
    const [copiedLink, setCopiedLink] = useState(false);
    const [player2Connected, setPlayer2Connected] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [showRestartConfirm, setShowRestartConfirm] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [showTurnNotification, setShowTurnNotification] = useState(false);
    const [showMobileChat, setShowMobileChat] = useState(false);
    const [lastReadMessageCount, setLastReadMessageCount] = useState(0);
    const [showGuessModal, setShowGuessModal] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [winner, setWinner] = useState(null);
    const transportRef = useRef(null);
    const hasAnnouncedReady = useRef(false);
    const chatEndRef = useRef(null);
    const prevActivePlayerRef = useRef(null);

    const myPlayer = q.get("player") === "2" ? 2 : 1;

    console.log('WhoDidItApp render - myPlayer:', myPlayer, 'player2Connected:', player2Connected, 'gameStarted:', gameStarted, 'shared:', !!shared);

    useEffect(() => {
        ensureRoomInUrl(room, myPlayer);
    }, [room, myPlayer]);

    useEffect(() => {
        console.log('Fetching champions for room:', room);
        fetch("https://ddragon.leagueoflegends.com/cdn/14.20.1/data/en_US/champion.json")
            .then(r => r.json())
            .then(data => {
                const champs = Object.values(data.data);
                const shuffled = champs.sort(() => 0.5 - Math.random()).slice(0, 36);
                console.log('Champions loaded and shuffled');
                setShared({
                    room,
                    order: shuffled,
                    fadedBy: { 1: {}, 2: {} },
                    activePlayer: 1,
                    seq: 0,
                    thieves: null
                });
            });
    }, [room]);

    const handleMessage = useCallback((msg) => {
        console.log('[Player', myPlayer, '] Handling message:', msg.type, msg);

        if (msg.type === "HELLO") {
            return;
        }

        if (msg.type === "PLAYER_READY") {
            console.log('[Player', myPlayer, '] Received PLAYER_READY from player', msg.player);
            // If I'm player 1 and I receive PLAYER_READY from player 2, or vice versa
            if (msg.player !== myPlayer) {
                console.log('[Player', myPlayer, '] Other player is ready! Setting player2Connected to true');
                setPlayer2Connected(true);
            }
            return;
        }

        if (msg.type === "START_GAME" && msg.state) {
            console.log('[Player', myPlayer, '] Game starting with state');
            setShared(msg.state);
            setLastSeenSeq(msg.state.seq);
            setGameStarted(true);
            setPlayer2Connected(true);
            return;
        }

        if (msg.type === "RESTART_GAME" && msg.state) {
            console.log('[Player', myPlayer, '] Game restarting with synchronized state');
            setShared(msg.state);
            setLastSeenSeq(msg.state.seq);
            setGameStarted(true);
            setShowRestartConfirm(false);
            setGameOver(false);
            setWinner(null);
            return;
        }

        if (msg.type === "CHAT_MESSAGE") {
            console.log('[Player', myPlayer, '] Received chat message from player', msg.player);
            // Only add message if it's from the other player (avoid duplicate)
            if (msg.player !== myPlayer) {
                setChatMessages(prev => [...prev, { player: msg.player, text: msg.text, timestamp: Date.now() }]);
            }
            return;
        }

        if (msg.type === "GAME_OVER" && msg.winner) {
            console.log('[Player', myPlayer, '] Game over! Winner:', msg.winner);
            setGameOver(true);
            setWinner(msg.winner);
            return;
        }

        if (msg.type === "STATE" && msg.state && msg.state.seq > lastSeenSeq) {
            console.log('[Player', myPlayer, '] Syncing state, seq:', msg.state.seq);
            setShared(msg.state);
            setLastSeenSeq(msg.state.seq);
        }
    }, [lastSeenSeq, myPlayer]);

    const transport = useRoomTransport(room, handleMessage);
    transportRef.current = transport;

    // Both players announce they're ready when they connect
    useEffect(() => {
        if (transportRef.current && shared && !hasAnnouncedReady.current) {
            const timer = setTimeout(() => {
                console.log(`[Player ${myPlayer}] Announcing PLAYER_READY`);
                transportRef.current.send({ type: "PLAYER_READY", player: myPlayer });
                hasAnnouncedReady.current = true;

                // If I'm player 1, also set that I'm ready (so the game can start when player 2 joins)
                if (myPlayer === 1) {
                    console.log('[Player 1] I am ready and waiting for player 2');
                }
            }, 1000); // Increased delay to ensure socket connection is fully established
            return () => clearTimeout(timer);
        }
    }, [myPlayer, shared]);

    // Player 1 starts the game automatically when Player 2 connects
    useEffect(() => {
        if (myPlayer === 1 && player2Connected && shared && !gameStarted && shared.thieves === null) {
            console.log('[Player 1] Both players ready, starting game automatically');
            const thieves = generateThieves(shared.order);
            const state = { ...shared, thieves };
            setShared(state);
            setGameStarted(true);
            console.log('[Player 1] Broadcasting START_GAME');
            transportRef.current.send({ type: "START_GAME", state });
        }
    }, [myPlayer, player2Connected, shared, gameStarted]);

    // Detect when turn changes to show notification
    useEffect(() => {
        if (shared && shared.activePlayer !== prevActivePlayerRef.current) {
            prevActivePlayerRef.current = shared.activePlayer;

            // Show notification if it's now my turn and game has started
            if (shared.activePlayer === myPlayer && gameStarted) {
                setShowTurnNotification(true);
                setTimeout(() => setShowTurnNotification(false), 3000);
            }
        }
    }, [shared, myPlayer, gameStarted]);

    // Mark messages as read when chat is opened on mobile
    useEffect(() => {
        if (showMobileChat) {
            setLastReadMessageCount(chatMessages.length);
        }
    }, [showMobileChat, chatMessages.length]);

    function inviteLink() {
        const base = window.location.origin + window.location.pathname;
        return `${base}?room=${room}&player=2`;
    }

    async function copyInviteLink() {
        try {
            await navigator.clipboard.writeText(inviteLink());
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }

    function issue(action) {
        const next = reduce(shared, action);
        setShared(next);
        setLastSeenSeq(next.seq);
        transport.send({ type: "STATE", state: next });
    }

    function onTileClick(id) {
        if (myPlayer !== shared.activePlayer) return;

        const isFaded = shared.fadedBy[myPlayer][id];

        if (isFaded) {
            // Unfade the champion
            issue({ type: "UNFADE", id, player: myPlayer });
        } else {
            // Fade the champion
            issue({ type: "FADE", id, player: myPlayer });
        }
    }

    function onPass() {
        if (myPlayer !== shared.activePlayer) return;
        issue({ type: "PASS" });
    }

    function confirmRestart() {
        console.log(`[Player ${myPlayer}] Initiating game restart`);
        // Fetch new champions and reset game state
        fetch("https://ddragon.leagueoflegends.com/cdn/14.20.1/data/en_US/champion.json")
            .then(r => r.json())
            .then(data => {
                const champs = Object.values(data.data);
                const shuffled = champs.sort(() => 0.5 - Math.random()).slice(0, 36);
                const thieves = generateThieves(shuffled);
                const newState = {
                    room,
                    order: shuffled,
                    fadedBy: { 1: {}, 2: {} },
                    activePlayer: 1,
                    seq: 0,
                    thieves
                };
                setShared(newState);
                setLastSeenSeq(0);
                setGameStarted(true);
                setShowRestartConfirm(false);
                setGameOver(false);
                setWinner(null);
                // Broadcast restart with the complete state to other player
                transportRef.current.send({ type: "RESTART_GAME", state: newState });
            });
    }

    function makeGuess(championId) {
        if (!shared || !shared.thieves) return;

        const opponentPlayer = myPlayer === 1 ? 2 : 1;
        const opponentThief = shared.thieves[opponentPlayer];

        console.log(`[Player ${myPlayer}] Making guess: ${championId}, opponent thief: ${opponentThief.id}`);

        if (championId === opponentThief.id) {
            // Correct guess - player wins!
            console.log(`[Player ${myPlayer}] CORRECT GUESS! I win!`);
            setGameOver(true);
            setWinner(myPlayer);
            transportRef.current.send({ type: "GAME_OVER", winner: myPlayer });
        } else {
            // Wrong guess - pass turn
            console.log(`[Player ${myPlayer}] Wrong guess, passing turn`);
            issue({ type: "PASS" });
        }

        setShowGuessModal(false);
    }

    // Auto-scroll chat to bottom when new messages arrive
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [chatMessages]);

    function sendChatMessage(e) {
        e.preventDefault();
        if (!chatInput.trim()) return;

        const message = { player: myPlayer, text: chatInput.trim(), timestamp: Date.now() };
        setChatMessages(prev => [...prev, message]);
        transportRef.current.send({ type: "CHAT_MESSAGE", player: myPlayer, text: chatInput.trim() });
        setChatInput("");
    }

    if (!shared) {
        return (
            <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="animate-pulse text-xl">Loading champions...</div>
                </div>
            </div>
        );
    }

    // Player 2 waiting for game to start
    if (myPlayer === 2 && !gameStarted) {
        return (
            <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="animate-pulse text-xl mb-2">Waiting for game to start...</div>
                    <div className="text-neutral-400 text-sm">Room: {room}</div>
                </div>
            </div>
        );
    }

    const myThief = shared.thieves ? shared.thieves[myPlayer] : null;
    const canPlay = gameStarted && myThief && !gameOver;
    const isMyTurn = shared.activePlayer === myPlayer;
    const unreadCount = chatMessages.length - lastReadMessageCount;
    const hasUnreadMessages = unreadCount > 0;

    return (
        <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col">
            {/* Game Over Modal */}
            {gameOver && winner && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                    <div className="bg-neutral-900 border-2 border-gray-500 rounded-xl p-8 max-w-md w-full text-center">
                        <h2 className={`text-3xl font-bold mb-4 ${winner === myPlayer ? `text-emerald-400` : `text-red-400`}`}>
                            {winner === myPlayer ? "YOU WIN!" : "YOU LOSE"}
                        </h2>
                        <p className="text-neutral-300 mb-6">
                            {winner === myPlayer
                                ? "You correctly guessed your opponent's thief!"
                                : "Your opponent guessed your thief correctly!"}
                        </p>
                        {myThief && shared.thieves && (
                            <div className="mb-6">
                                <p className="text-sm text-neutral-400 mb-2">The thieves were:</p>
                                <div className="flex gap-4 justify-center">
                                    <div className="flex flex-col items-center">
                                        <img
                                            src={`https://ddragon.leagueoflegends.com/cdn/14.20.1/img/champion/${shared.thieves[1].image.full}`}
                                            alt={shared.thieves[1].name}
                                            className="w-16 h-16 rounded mb-1"
                                        />
                                        <span className="text-xs text-neutral-400">Player 1</span>
                                        <span className="text-sm font-semibold">{shared.thieves[1].name}</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <img
                                            src={`https://ddragon.leagueoflegends.com/cdn/14.20.1/img/champion/${shared.thieves[2].image.full}`}
                                            alt={shared.thieves[2].name}
                                            className="w-16 h-16 rounded mb-1"
                                        />
                                        <span className="text-xs text-neutral-400">Player 2</span>
                                        <span className="text-sm font-semibold">{shared.thieves[2].name}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={() => setShowRestartConfirm(true)}
                            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors font-semibold"
                        >
                            Play Again
                        </button>
                    </div>
                </div>
            )}

            {/* Guess Modal */}
            {showGuessModal && !gameOver && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 max-w-4xl w-full my-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Make Your Guess</h2>
                            <button
                                onClick={() => setShowGuessModal(false)}
                                className="text-neutral-400 hover:text-neutral-200 p-2"
                            >
                                <Check className="w-6 h-6" />
                            </button>
                        </div>
                        <p className="text-neutral-400 text-sm mb-4">
                            Select the champion you think is your opponent's thief. If you're correct, you win! If you're wrong, your turn ends.
                        </p>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-[60vh] overflow-y-auto p-2">
                            {shared.order.map(ch => (
                                <button
                                    key={ch.id}
                                    onClick={() => makeGuess(ch.id)}
                                    className="aspect-square w-full rounded border border-neutral-700 p-2 flex flex-col items-center justify-center transition-all hover:border-emerald-500 hover:bg-emerald-900/20"
                                >
                                    <img
                                        src={`https://ddragon.leagueoflegends.com/cdn/14.20.1/img/champion/${ch.image.full}`}
                                        alt={ch.name}
                                        className="w-12 h-12 rounded mb-1"
                                    />
                                    <div className="text-xs text-neutral-300 text-center leading-tight">{ch.name}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Turn Notification Banner */}
            {showTurnNotification && canPlay && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-bounce px-4">
                    <div className="bg-emerald-600 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg shadow-lg border-2 border-emerald-400 flex items-center gap-2 sm:gap-3">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full animate-pulse"></div>
                        <span className="text-base sm:text-lg font-bold">YOUR TURN!</span>
                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full animate-pulse"></div>
                    </div>
                </div>
            )}

            {/* Mobile Chat Toggle Button - Fixed at bottom on mobile */}
            <button
                onClick={() => setShowMobileChat(!showMobileChat)}
                className="lg:hidden fixed bottom-4 right-4 z-40 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg flex items-center gap-2 transition-all"
            >
                <Send className="w-5 h-5" />
                {hasUnreadMessages && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                        {unreadCount}
                    </span>
                )}
            </button>

            {/* Mobile Chat Modal */}
            {showMobileChat && (
                <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
                    <div className="bg-neutral-900 w-full h-[80vh] rounded-t-2xl flex flex-col">
                        <div className="p-4 border-b border-neutral-800 flex justify-between items-center">
                            <div>
                                <h2 className="text-lg font-semibold">Chat</h2>
                                <p className="text-xs text-neutral-500">Room: {room}</p>
                            </div>
                            <button
                                onClick={() => setShowMobileChat(false)}
                                className="text-neutral-400 hover:text-neutral-200 p-2"
                            >
                                <Check className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Chat messages - scrollable */}
                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="flex flex-col gap-3">
                                {chatMessages.length === 0 ? (
                                    <div className="text-center text-neutral-500 text-sm mt-8">
                                        No messages yet. Say hi! 👋
                                    </div>
                                ) : (
                                    chatMessages.map((msg, index) => (
                                        <div key={index} className={`flex flex-col gap-1 ${msg.player === myPlayer ? "items-end" : "items-start"}`}>
                                            <div className="text-xs text-neutral-500">
                                                Player {msg.player}
                                            </div>
                                            <div className={`text-sm rounded-lg py-2 px-3 max-w-[85%] break-words ${
                                                msg.player === myPlayer 
                                                    ? "bg-blue-600 text-white" 
                                                    : "bg-neutral-800 text-neutral-200"
                                            }`}>
                                                {msg.text}
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={chatEndRef} />
                            </div>
                        </div>

                        {/* Chat input - fixed at bottom */}
                        <div className="p-4 border-t border-neutral-800 bg-neutral-900">
                            <form onSubmit={sendChatMessage} className="flex gap-2">
                                <input
                                    type="text"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    className="flex-1 px-3 py-3 bg-neutral-800 text-neutral-100 rounded-lg border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                                    placeholder="Type a message..."
                                />
                                <button
                                    type="submit"
                                    disabled={!chatInput.trim()}
                                    className="p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Main game area */}
            <div className="flex-1 flex flex-col lg:flex-row items-start p-4 sm:p-6 gap-4 lg:gap-6 max-w-6xl mx-auto w-full">
                {/* Left side - Game */}
                <div className="flex-1 w-full flex flex-col items-center">
                    <h1 className="text-xl sm:text-2xl font-semibold mb-4 text-center">{APP_NAME}</h1>

                    {/* Restart Confirmation Modal */}
                    {showRestartConfirm && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 max-w-md w-full">
                                <h2 className="text-xl font-semibold mb-3">Restart Game?</h2>
                                <p className="text-neutral-400 mb-6">
                                    This will start a new game with new champions and thieves. Both players will be affected.
                                </p>
                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={() => setShowRestartConfirm(false)}
                                        className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 rounded transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmRestart}
                                        className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded transition-colors"
                                    >
                                        Restart
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Player 1 invite section - shown before game starts */}
                    {myPlayer === 1 && !gameStarted && (
                        <div className="w-full mb-6 p-4 bg-neutral-900 border border-neutral-800 rounded-lg">
                            <div className="flex flex-col gap-3">
                                <div className="text-xs sm:text-sm text-neutral-400">
                                    Room: <span className="text-neutral-200 font-mono break-all">{room}</span>
                                </div>
                                <div className="flex gap-2 items-center flex-wrap">
                                    <button
                                        onClick={copyInviteLink}
                                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded transition-colors text-sm sm:text-base"
                                    >
                                        {copiedLink ? (
                                            <>
                                                <Check className="w-4 h-4" />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-4 h-4" />
                                                Copy Invite Link
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="text-xs sm:text-sm text-neutral-500">
                                    {player2Connected ? (
                                        <span className="text-emerald-400 animate-pulse">Player 2 connected! Starting game...</span>
                                    ) : (
                                        "Waiting for Player 2 to join..."
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Turn indicator */}
                    {canPlay && (
                        <div className={`w-full mb-4 p-3 sm:p-4 rounded-lg border-2 transition-all ${
                            isMyTurn 
                                ? "bg-emerald-900/30 border-emerald-500" 
                                : "bg-neutral-900/30 border-neutral-700"
                        }`}>
                            <div className="flex items-center justify-center gap-2 sm:gap-3">
                                {isMyTurn ? (
                                    <>
                                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                                        <span className="text-sm sm:text-lg font-semibold text-emerald-400 text-center">Your Turn - Make Your Move!</span>
                                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-neutral-500 rounded-full"></div>
                                        <span className="text-sm sm:text-lg text-neutral-400 text-center">Opponent's Turn - Please Wait</span>
                                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-neutral-500 rounded-full"></div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Game grid */}
                    <Grid
                        order={shared.order}
                        fadedBy={shared.fadedBy}
                        player={myPlayer}
                        activePlayer={shared.activePlayer}
                        onTileClick={onTileClick}
                        disabled={!canPlay}
                    />

                    {/* Game controls */}
                    <div className="mt-4 flex gap-2 sm:gap-4 items-center flex-wrap justify-center w-full px-2">
                        <button
                            onClick={() => setShowGuessModal(true)}
                            disabled={!canPlay || !isMyTurn}
                            className="px-3 sm:px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-semibold"
                        >
                            Make a Guess
                        </button>
                        <button
                            onClick={onPass}
                            disabled={!canPlay || !isMyTurn}
                            className="px-3 sm:px-4 py-2 bg-neutral-800 hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                        >
                            Pass Turn
                        </button>
                        {canPlay && (
                            <button
                                onClick={() => setShowRestartConfirm(true)}
                                className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 rounded transition-colors text-sm sm:text-base"
                            >
                                Restart
                            </button>
                        )}
                        {myThief && (
                            <div className="flex items-center gap-2 bg-neutral-900 border border-neutral-800 px-3 sm:px-4 py-2 rounded">
                                <img
                                    src={`https://ddragon.leagueoflegends.com/cdn/14.20.1/img/champion/${myThief.image.full}`}
                                    alt={myThief.name}
                                    className="w-6 h-6 sm:w-8 sm:h-8 rounded"
                                />
                                <div className="flex flex-col">
                                    <span className="text-xs text-neutral-400">Your Thief</span>
                                    <span className="text-xs sm:text-sm font-semibold">{myThief.name}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right side - Chat sidebar (desktop only) */}
                <div className="lg:mt-12 hidden lg:flex w-96 flex-col bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden" style={{ height: 'fit-content', maxHeight: '800px' }}>
                    <div className="p-4 border-b border-neutral-800 bg-neutral-900/50">
                        <h2 className="text-lg font-semibold">Chat</h2>
                        <p className="text-xs text-neutral-500">Room: {room}</p>
                    </div>

                    {/* Chat messages - scrollable */}
                    <div className="flex-1 overflow-y-auto p-4 min-h-[400px]">
                        <div className="flex flex-col gap-3">
                            {chatMessages.length === 0 ? (
                                <div className="text-center text-neutral-500 text-sm mt-8">
                                    No messages yet. Say hi! 👋
                                </div>
                            ) : (
                                chatMessages.map((msg, index) => (
                                    <div key={index} className={`flex flex-col gap-1 ${msg.player === myPlayer ? "items-end" : "items-start"}`}>
                                        <div className="text-xs text-neutral-500">
                                            Player {msg.player}
                                        </div>
                                        <div className={`text-sm rounded-lg py-2 px-3 max-w-[85%] break-words ${
                                            msg.player === myPlayer 
                                                ? "bg-blue-600 text-white" 
                                                : "bg-neutral-800 text-neutral-200"
                                        }`}>
                                            {msg.text}
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={chatEndRef} />
                        </div>
                    </div>

                    {/* Chat input - fixed at bottom */}
                    <div className="p-4 border-t border-neutral-800 bg-neutral-900/50">
                        <form onSubmit={sendChatMessage} className="flex gap-2">
                            <input
                                type="text"
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                className="flex-1 px-3 py-2 bg-neutral-800 text-neutral-100 rounded-lg border border-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                                placeholder="Type a message..."
                            />
                            <button
                                type="submit"
                                disabled={!chatInput.trim()}
                                className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function reduce(state, action) {
    switch (action.type) {
        case "FADE": {
            const fadedBy = { ...state.fadedBy, [action.player]: { ...state.fadedBy[action.player], [action.id]: true } };
            return { ...state, fadedBy, seq: state.seq + 1 };
        }
        case "UNFADE": {
            const playerFaded = { ...state.fadedBy[action.player] };
            delete playerFaded[action.id];
            const fadedBy = { ...state.fadedBy, [action.player]: playerFaded };
            return { ...state, fadedBy, seq: state.seq + 1 };
        }
        case "PASS":
            return { ...state, activePlayer: state.activePlayer === 1 ? 2 : 1, seq: state.seq + 1 };
        default:
            return state;
    }
}

function Grid({ order, fadedBy, player, activePlayer, onTileClick, disabled }) {
    const fadedIds = player ? fadedBy[player] : {};
    return (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1 sm:gap-2 w-full max-w-2xl px-2 sm:px-0">
            {order.map(ch => (
                <Tile
                    key={ch.id}
                    ch={ch}
                    faded={!!fadedIds[ch.id]}
                    onClick={() => onTileClick(ch.id)}
                    active={player === activePlayer && !disabled}
                />
            ))}
        </div>
    );
}

function Tile({ ch, faded, onClick, active }) {
    return (
        <button
            onClick={onClick}
            disabled={!active}
            className={`aspect-square w-full rounded border border-neutral-700 p-1 sm:p-2 flex flex-col items-center justify-center transition-all ${
                faded 
                    ? "opacity-10 cursor-pointer" 
                    : active 
                        ? "hover:opacity-80 hover:border-blue-500 cursor-pointer active:scale-95" 
                        : "opacity-60 cursor-not-allowed"
            }`}
        >
            <img
                src={`https://ddragon.leagueoflegends.com/cdn/14.20.1/img/champion/${ch.image.full}`}
                alt={ch.name}
                className="w-8 h-8 sm:w-12 sm:h-12 rounded mb-0.5 sm:mb-1"
            />
            <div className="text-[0.6rem] sm:text-xs text-neutral-300 text-center leading-tight">{ch.name}</div>
        </button>
    );
}

function generateThieves(order) {
    const copy = [...order];
    const t1 = copy[Math.floor(Math.random() * copy.length)];
    let t2 = t1;
    while (t2.id === t1.id) {
        t2 = copy[Math.floor(Math.random() * copy.length)];
    }
    return { 1: t1, 2: t2 };
}
